import type { BroadsideShotResult } from "../types";
import { mediaUrl } from "./media-url";

export const BROADSIDE_HIT_SOUNDS = [
  mediaUrl("/assets/audio/broadside/explosion-hit-01.mp3"),
  mediaUrl("/assets/audio/broadside/explosion-hit-02.mp3"),
  mediaUrl("/assets/audio/broadside/explosion-hit-03.mp3"),
  mediaUrl("/assets/audio/broadside/explosion-hit-04.mp3"),
] as const;

export const BROADSIDE_MISS_SOUNDS = [
  mediaUrl("/assets/audio/broadside/water-miss-01.mp3"),
  mediaUrl("/assets/audio/broadside/water-miss-02.mp3"),
] as const;

export const BROADSIDE_IMPACT_SOUNDS = [...BROADSIDE_HIT_SOUNDS, ...BROADSIDE_MISS_SOUNDS] as const;

type AudioClip = { source: string; voices: HTMLAudioElement[]; nextVoice: number };
type BroadsideAudioBank = { clips: Map<string, AudioClip> };

let sharedBank: BroadsideAudioBank | undefined;
let preloadJob: Promise<void> | undefined;
const playedShots = new Set<string>();

function textSeed(value: string) {
  let seed = 2166136261;
  for (let index = 0; index < value.length; index += 1) { seed ^= value.charCodeAt(index); seed = Math.imul(seed, 16777619); }
  return seed >>> 0;
}

function sourcesForResult(result: BroadsideShotResult) {
  return result === "miss" ? BROADSIDE_MISS_SOUNDS : BROADSIDE_HIT_SOUNDS;
}

/** Stable variation keeps every connected display on the same impact clip. */
export function broadsideImpactSoundUrl(shotId: string, result: BroadsideShotResult) {
  const sources = sourcesForResult(result);
  return sources[textSeed(shotId) % sources.length];
}

function audioBank() {
  if (sharedBank || typeof Audio === "undefined") return sharedBank;
  sharedBank = {
    clips: new Map(BROADSIDE_IMPACT_SOUNDS.map((source) => [source, {
      source,
      nextVoice: 0,
      voices: Array.from({ length: 2 }, () => {
        const voice = new Audio(source); voice.preload = "auto"; voice.load(); return voice;
      }),
    }])),
  };
  return sharedBank;
}

function waitForDecodedMedia(voice: HTMLAudioElement) {
  if (voice.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    let timeout = 0;
    const cleanup = () => { voice.removeEventListener("canplay", ready); voice.removeEventListener("error", failed); if (timeout) clearTimeout(timeout); };
    const ready = () => { cleanup(); resolve(); };
    const failed = () => { cleanup(); reject(new Error(`Could not preload ${voice.src}`)); };
    voice.addEventListener("canplay", ready, { once: true }); voice.addEventListener("error", failed, { once: true });
    timeout = globalThis.setTimeout(() => { cleanup(); reject(new Error(`Timed out preloading ${voice.src}`)); }, 8_000) as unknown as number;
    voice.load();
  });
}

/** Fetches and browser-decodes every impact variation before the first salvo. */
export function preloadBroadsideAudio() {
  if (preloadJob) return preloadJob;
  const bank = audioBank();
  if (!bank) return Promise.resolve();
  preloadJob = Promise.all([...bank.clips.values()].flatMap((clip) => clip.voices.map(waitForDecodedMedia))).then(() => undefined);
  return preloadJob;
}

/** Called by the Player Display's explicit Arm gesture to unlock media playback. */
export async function unlockBroadsideAudio() {
  const bank = audioBank(); const voice = bank?.clips.values().next().value?.voices[0] as HTMLAudioElement | undefined;
  if (!voice) return;
  const previousVolume = voice.volume; voice.volume = 0;
  try { await voice.play(); } catch { /* A later armed impact can retry. */ }
  voice.pause(); voice.currentTime = 0; voice.volume = previousVolume;
}

export function playBroadsideImpactSound(shotId: string, result: BroadsideShotResult) {
  const source = broadsideImpactSoundUrl(shotId, result);
  if (playedShots.has(shotId)) return source;
  playedShots.add(shotId); if (playedShots.size > 128) playedShots.delete(playedShots.values().next().value!);
  const clip = audioBank()?.clips.get(source); if (!clip) return source;
  const idleVoice = clip.voices.findIndex((voice) => voice.paused || voice.ended); const voiceIndex = idleVoice >= 0 ? idleVoice : clip.nextVoice;
  clip.nextVoice = (voiceIndex + 1) % clip.voices.length; const voice = clip.voices[voiceIndex]; voice.volume = result === "miss" ? .82 : .94;
  try { voice.currentTime = 0; void voice.play().catch(() => undefined); } catch { /* Visual impact must continue if audio is unavailable. */ }
  return source;
}
